import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LuPen, LuTrash2 } from 'react-icons/lu';
import RelationshipTabs from '../components/RelationshipTabs';
import RelationshipOverviewContent from '../components/RelationshipOverviewContent';
import RelationshipSummaryCard from '../components/RelationshipSummaryCard';
import { useAuth } from '../context/AuthContext';
import { useContact } from '../hooks/useContact';
import { useContacts } from '../hooks/useContacts';
import { updateContactFavorite } from '../services/contacts';

const RelationshipOverviewPage = () => {
  const { id } = useParams();
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const { data: contacts = [], isLoading: contactsLoading } = useContacts(accessToken);
  const baseContact = useMemo(
    () => contacts.find((c) => String(c.id) === String(id)),
    [contacts, id]
  );
  const {
    data: contactFull,
    isLoading: contactLoading,
    isFetching,
    isError,
  } = useContact(accessToken, id, baseContact);
  const contact = useMemo(() => {
    if (baseContact && contactFull) return { ...baseContact, ...contactFull };
    return contactFull || baseContact;
  }, [baseContact, contactFull]);
  const isLoading = contactLoading || contactsLoading;

  const [openMenuId, setOpenMenuId] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    const handleClickAway = () => setOpenMenuId(null);
    window.addEventListener('click', handleClickAway);
    return () => window.removeEventListener('click', handleClickAway);
  }, []);

  const favoriteMutation = useMutation({
    mutationFn: (isFavorite) => updateContactFavorite(accessToken, id, isFavorite),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', id] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  const toggleFavorite = () => {
    if (!contact || favoriteMutation.isPending) return;
    favoriteMutation.mutate(!contact.is_favorite);
  };

  const notes = Array.isArray(contact?.notes) ? contact.notes : [];
  const recentNotes = [...notes]
    .sort((a, b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0))
    .slice(0, 3);

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="sticky top-0 z-20 border-b border-white/5 bg-[#050815] px-4 pb-3 pt-16 sm:px-6 md:px-8 md:pt-10">
        <RelationshipTabs contactId={id} activeKey="overview" />
      </div>
      <div className="px-4 py-6 sm:px-6 md:px-8 md:py-10">
      <div className="space-y-4">
        <div className="rounded-2xl border border-white/10 bg-[#0c1428]/90 p-6 shadow-soft">
          {isLoading ? (
            <p className="text-sm text-slate-200">Loading contact…</p>
          ) : isError ? (
            <p className="text-sm text-amber-300">Unable to load contact details.</p>
          ) : contact ? (
            <RelationshipOverviewContent
              contact={contact}
              onToggleFavorite={toggleFavorite}
              isFavoritePending={favoriteMutation.isPending}
            />
          ) : (
            <p className="text-sm text-slate-200">Contact not found.</p>
          )}
        </div>
      </div>

      <div className="mt-6 space-y-6">
        <RelationshipSummaryCard contact={contact} contactLoading={isLoading} contactFetching={isFetching} />

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-200">Recent Notes</h3>
          {recentNotes.length === 0 ? (
            <p className="text-sm text-slate-400">No notes yet for this contact.</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3" onClick={(e) => e.stopPropagation()}>
              {recentNotes.map((note, idx) => {
                const noteId = note.id || `note-${idx}`;
                const isOpen = openMenuId === noteId;
                return (
                  <div
                    key={noteId}
                    className="flex h-60 flex-col rounded-xl bg-amber-300 text-slate-900 shadow-lg ring-1 ring-amber-200"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between border-b border-amber-400/60 px-3 py-2">
                      <p className="text-sm font-semibold line-clamp-1">
                        {(() => {
                          const dt = note.created_at || note.createdAt;
                          if (!dt) return '—';
                          const d = new Date(dt);
                          if (Number.isNaN(d.getTime())) return '—';
                          return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
                        })()}
                      </p>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(isOpen ? null : noteId);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-800 hover:bg-amber-200/80"
                          aria-label="Note actions"
                        >
                          ⋯
                        </button>
                        <div
                          className={`${isOpen ? 'block' : 'hidden'} absolute right-0 top-9 z-10 w-36 rounded-lg border border-amber-400/60 bg-amber-200/95 text-slate-900 shadow-lg`}
                        >
                          <button
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-amber-300/80"
                            type="button"
                          >
                            <LuPen className="h-4 w-4 text-sky-600" />
                            <span>Edit note</span>
                          </button>
                          <button
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-amber-300/80"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPendingDeleteId(noteId);
                              setOpenMenuId(null);
                            }}
                          >
                            <LuTrash2 className="h-4 w-4 text-rose-600" />
                            <span>Delete note</span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 px-3 py-3">
                      <p className="h-full overflow-y-auto whitespace-pre-line text-sm leading-relaxed">
                        {note.content || note.body || '—'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      </div>

      {pendingDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" onClick={() => setPendingDeleteId(null)}>
          <div
            className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0c1428]/95 p-5 shadow-soft"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-slate-50">Delete this note?</h3>
            <p className="mt-2 text-sm text-slate-300">This action cannot be undone.</p>
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={() => setPendingDeleteId(null)}
                className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  // TODO: wire delete note API
                  setPendingDeleteId(null);
                }}
                className="rounded-lg bg-rose-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-rose-500/30"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelationshipOverviewPage;
